import {BaseApi} from './BaseApi'
import type {ActorFills, ActorHistory, ActorSplitOrders, PlacedHoursFigures} from './teamApi'
import {reportCatchError} from '../utils/errorReporter'
import type {CompanyEmployeeHoursResponse} from '~/src/components/platform/company/tabs/metrics/types'

// =============================================
// Dashboard KPI Types
// =============================================

export interface DashboardKPI {
  label: string
  value: number
  previousValue?: number
  changePercent?: number
  trend?: 'up' | 'down' | 'stable'
  format?: 'number' | 'percent' | 'currency' | 'hours'
}

export interface DashboardSummaryResponse {
  periodStart: string
  periodEnd: string
  kpis: {
    totalWorkersFilled: DashboardKPI
    totalHoursWorked: DashboardKPI
    totalEarnings: DashboardKPI
    fillRatio: DashboardKPI
    qualityFillRatio: DashboardKPI
    noShowRate: DashboardKPI
    activeOrders: DashboardKPI
    activeAgencies: DashboardKPI
  }
  trends: TrendDataPoint[]
}

export interface TrendDataPoint {
  date: string
  fills: number
  noShows: number
  earnings: number
  hours: number
}

// =============================================
// Earnings Types
// =============================================

export interface EarningsSummaryResponse {
  periodStart: string
  periodEnd: string
  totalEarnings: number
  totalHours: number
  averageEarningsPerWorker: number
  breakdown: EarningsBreakdown[]
}

export interface EarningsBreakdown {
  period: string
  earnings: number
  hoursWorked: number
  workerCount: number
}

// =============================================
// Agency Metrics Types
// =============================================

export interface AgencyRankingResponse {
  rank: number
  agencyId: string
  agencyName?: string
  compositeScore: number
  fillRatio: number
  qualityFillRatio: number
  noShowRate: number
  avgResponseTimeMs: number
  totalFills: number
  totalOrders: number
}

export interface AgencyReliabilityScore {
  agencyId: string
  calculatedAt: string
  periodStart: string
  periodEnd: string
  fillRatioScore: number
  qualityFillScore: number
  noShowRateScore: number
  overPledgeRateScore: number
  responseTimeScore: number
  compositeScore: number
  metrics?: AgencyReliabilityMetrics
}

export interface AgencyReliabilityMetrics {
  totalOrders: number
  totalFilled: number
  totalRequested: number
  totalNoShows: number
  totalQualityFills: number
  avgResponseTimeMs: number
  overPledgeCount: number
  totalReorders: number
  fillRatio: number
  qualityFillRatio: number
  noShowRate: number
}

// =============================================
// Recruiter Leaderboard Types (Ticket 2, S12)
// =============================================

/**
 * One window's team figures on a recruiter leaderboard, over every listed person (the agency board's
 * roster, or the platform board's actors), not only the rows returned. Each is S6's actor figure, so
 * the boards and the staffer page cannot disagree. See
 * docs/deep-dive/staffer-performance-metrics.md §14.11.
 *
 * Every figure is optional: a server may omit one for a viewer who may not see it, and the page then
 * hides it rather than showing a failure.
 */
export interface RecruiterTeamFigures {
  /** ISO bounds of the window. */
  startDate: string
  endDate: string
  /**
   * The workers the listed people accepted onto filled orders dated in the window. A worker accepted
   * in one order by two people counts once for each.
   */
  placements?: number
  /** Of `placements`, those on backup groups. */
  backupPlacements?: number
  /** Listed people with at least one placement. */
  staffersWithFills?: number
  /** placements ÷ staffersWithFills, to a tenth. Null when no one placed a worker. */
  avgPlacementsPerStafferWithFills?: number | null
}

export interface RecruiterTeam {
  /** People on the board: the agency's listed members, or everyone who accepted a worker. */
  people?: number
  /** Null when the placements read failed. */
  current: RecruiterTeamFigures | null
  /** Present only when a prior window was requested; null when its read failed. */
  prior?: RecruiterTeamFigures | null
}

/**
 * One person on a leaderboard. A figure is null when its read failed, and absent when the server did
 * not send it: an older server, or a viewer who may not see it (Agency-category viewers get no split
 * orders or placed worker hours).
 */
export interface RecruiterRow {
  userId: string
  name: string
  /** Competition rank on Total fills (1, 2, 2, 4). Null for no fills, or when fills failed. */
  rank: number | null
  fills?: ActorFills | null
  splitOrders?: ActorSplitOrders | null
  placedHours?: PlacedHoursFigures | null
  /** The person's primary agency: the platform board only. */
  agencyId?: string
  agencyName?: string
}

/** A recruiter leaderboard: the platform board's body, and the S12 part of the agency board's. */
export interface RecruiterLeaderboard {
  periodStart: string
  periodEnd: string
  /** From when acceptances carry their acceptor's id, so which windows are complete. */
  actorHistory: ActorHistory
  team: RecruiterTeam
  /** At most `limit`, ordered by Total fills, then name. */
  rows: RecruiterRow[]
  /** Listed people not returned as rows. */
  omittedPeople?: number
}

/** Options for a leaderboard read. */
export interface RecruiterLeaderboardOptions {
  /** The window team Total fills is compared with. Omitted, the response has no prior figures. */
  prior?: DateRange
  /** Rows to return; the server clamps it to 1–50. Defaults to 20. */
  limit?: number
}

/** A leaderboard read's query: ISO bounds, the limit, and the prior window only when one is given. */
function leaderboardParams(
  startDate: Date,
  endDate: Date,
  {prior, limit = 20}: RecruiterLeaderboardOptions,
): Record<string, string | number> {
  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    limit,
    ...(prior && {
      priorStartDate: prior.startDate.toISOString(),
      priorEndDate: prior.endDate.toISOString(),
    }),
  }
}

// =============================================
// Company Retention Types
// =============================================

export interface CompanyRetentionResponse {
  periodStart: string
  periodEnd: string
  totalCompanies: number
  activeCompanies: number
  retentionRate: number
  newCompanies: number
  churnedCompanies: number
  topCompaniesByOrders: CompanyMetric[]
  atRiskCompanies: AtRiskCompany[]
}

export interface CompanyMetric {
  companyId: string
  companyName?: string
  orderCount: number
  fillSuccessRate: number
  totalSpend: number
}

export interface AtRiskCompany {
  companyId: string
  companyName?: string
  lastOrderDate: string
  orderDeclinePercent: number
  riskScore: number
}

// =============================================
// Fill Metrics Types
// =============================================

export interface FillRatioResult {
  requestedCount: number
  filledCount: number
  fillRatio: number
  byAgency?: AgencyFillBreakdown[]
}

export interface AgencyFillBreakdown {
  agencyId: string
  agencyName?: string
  requested: number
  filled: number
  fillRatio: number
}

export interface QualityFillRatioResult {
  requestedCount: number
  filledCount: number
  qualityFillCount: number
  noShowCount: number
  qualityFillRatio: number
  byAgency?: AgencyQualityBreakdown[]
}

export interface AgencyQualityBreakdown {
  agencyId: string
  agencyName?: string
  filled: number
  qualityFills: number
  noShows: number
  qualityFillRatio: number
}

export interface FillVelocityResult {
  periodStart: string
  periodEnd: string
  totalFills: number
  hoursInPeriod: number
  fillsPerHour: number
  targetBenchmark: number
  meetsBenchmark: boolean
}

// =============================================
// Attendance Types
// =============================================

export interface WorkerSuspensionStatus {
  suspended: boolean
}

// Attendance types have a single source of truth in the metrics tab's types module.
// Re-exported here so metricsApi consumers keep importing from one place while the
// shapes cannot drift from what the component/tests use.
export type {
  AttendanceClockState,
  AttendanceAlerts,
  AttendanceSummary,
  AttendanceRosterEntry,
  AttendanceAbsentEntry,
  AttendanceGhostPunch,
  AttendanceDepartmentBreakdown,
  AttendanceData,
  AttendanceMeta,
  CompanyAttendanceApiEnvelope,
  CompanyEmployeeHoursSite,
  CompanyEmployeeHoursRow,
  CompanyEmployeeHoursResponse,
} from '~/src/components/platform/company/tabs/metrics/types'

// =============================================
// Company-Specific Metrics Types
// =============================================

export interface CompanyMetricsKPI {
  label: string
  value: number
  previousValue?: number
  changePercent?: number
  trend?: 'up' | 'down' | 'stable'
  format?: 'number' | 'percent' | 'currency' | 'hours'
}

export interface CompanyMetricsOverviewResponse {
  companyId: string
  companyName: string
  periodStart: string
  periodEnd: string
  totalSpendYTD: CompanyMetricsKPI
  totalSpendMTD: CompanyMetricsKPI
  activeWorkers: CompanyMetricsKPI
  ordersThisMonth: CompanyMetricsKPI
  fillRate: CompanyMetricsKPI
  qualityFillRate: CompanyMetricsKPI
  noShowRate: CompanyMetricsKPI
  avgTimeToFill: CompanyMetricsKPI
  totalHoursWorkedMTD: CompanyMetricsKPI
  accountHealthScore: CompanyMetricsKPI
}

export interface InvoiceBreakdown {
  period: string
  totalBill: number
  totalPay: number
  margin: number
  invoiceCount: number
}

export interface DepartmentSpend {
  departmentId: string
  departmentName: string
  totalSpend: number
  percentage: number
}

export interface PositionSpend {
  positionId: string
  positionName: string
  totalSpend: number
  hoursWorked: number
  avgRate: number
}

export interface CompanyFinancialMetricsResponse {
  companyId: string
  periodStart: string
  periodEnd: string
  totalBilledAmount: number
  totalPayAmount: number
  grossMargin: number
  averageMarkupPercent: number
  averageInvoiceSize: number
  totalInvoices: number
  pendingInvoices: number
  approvedInvoices: number
  outstandingBalance: number
  monthlyBreakdown: InvoiceBreakdown[]
  spendByDepartment: DepartmentSpend[]
  spendByPosition: PositionSpend[]
}

export interface AgencyPerformanceForCompany {
  agencyId: string
  agencyName: string
  fillRate: number
  qualityFillRate: number
  noShowRate: number
  avgResponseTimeMinutes: number
  totalFills: number
  totalOrders: number
  reliabilityScore: number
}

export interface OrderStatusBreakdown {
  status: string
  count: number
  percentage: number
}

export interface FillRateTrend {
  date: string
  fillRate: number
  qualityFillRate: number
  ordersCount: number
  fillsCount: number
}

export interface CompanyOrdersMetricsResponse {
  companyId: string
  periodStart: string
  periodEnd: string
  totalOrders: number
  ordersThisPeriod: number
  averageOrderSize: number
  orderCompletionRate: number
  cancelledOrders: number
  fillRate: number
  qualityFillRate: number
  avgFillTimeHours: number
  sameDayFillRate: number
  reorderRate: number
  ordersByStatus: OrderStatusBreakdown[]
  agencyPerformance: AgencyPerformanceForCompany[]
  fillRateTrend: FillRateTrend[]
}

export interface TopWorker {
  workerId: string
  workerName: string
  agencyId: string
  agencyName: string
  totalEarnings: number
  totalHours: number
  attendanceRate: number
  tenure: number
  positionsWorked: number
}

export interface PositionAnalytics {
  positionId: string
  positionName: string
  orderCount: number
  fillRate: number
  avgPayRate: number
  avgFillTimeHours: number
  totalHoursWorked: number
}

export interface HoursBreakdown {
  date: string
  regularHours: number
  overtimeHours: number
  doubleTimeHours: number
  totalHours: number
}

export interface CompanyWorkforceMetricsResponse {
  companyId: string
  periodStart: string
  periodEnd: string
  totalUniqueWorkers: number
  activeWorkers: number
  newWorkersThisMonth: number
  workerTurnoverRate: number
  averageTenureDays: number
  retentionRate: number
  totalHoursWorked: number
  regularHours: number
  overtimeHours: number
  doubleTimeHours: number
  overtimePercentage: number
  attendanceRate: number
  noShowCount: number
  noShowRate: number
  topEarners: TopWorker[]
  mostHoursWorked: TopWorker[]
  bestAttendance: TopWorker[]
  positionAnalytics: PositionAnalytics[]
  hoursTrend: HoursBreakdown[]
}

export interface PeriodComparison {
  metric: string
  currentValue: number
  previousValue: number
  changePercent: number
  trend: 'up' | 'down' | 'stable'
}

export interface MonthlyTrend {
  month: string
  spend: number
  orders: number
  fills: number
  workers: number
  hours: number
  fillRate: number
  noShowRate: number
}

export interface CompanyTrendsMetricsResponse {
  companyId: string
  periodStart: string
  periodEnd: string
  spendGrowthRate: number
  orderVolumeGrowth: number
  fillRateChange: number
  workforceGrowth: number
  periodComparisons: PeriodComparison[]
  monthlyTrends: MonthlyTrend[]
  peakMonth?: string
  lowMonth?: string
  projectedNextMonthSpend?: number
  churnRiskScore?: number
}

export interface CompanyMetricsAccessResponse {
  canAccess: boolean
  agencyIds: string[] | null
}

// =============================================
// Date Range Helper
// =============================================

export interface DateRange {
  startDate: Date
  endDate: Date
}

// =============================================
// Agency Dashboard Types
// =============================================

export interface AgencyKPI {
  label: string
  value: number
  previousValue?: number
  changePercent?: number
  trend?: 'up' | 'down' | 'stable'
  format?: 'number' | 'percent' | 'currency' | 'hours' | 'time'
}

export interface AgencyDashboardSummaryResponse {
  agencyId: string
  agencyName?: string
  periodStart: string
  periodEnd: string
  kpis: {
    totalFills: AgencyKPI
    totalHoursWorked: AgencyKPI
    fillRatio: AgencyKPI
    qualityFillRatio: AgencyKPI
    noShowRate: AgencyKPI
    avgResponseTime: AgencyKPI
    activeOrders: AgencyKPI
    reliabilityScore: AgencyKPI
  }
  scoreBreakdown: {
    fillRatioScore: number
    qualityFillScore: number
    noShowRateScore: number
    overPledgeRateScore: number
    responseTimeScore: number
  }
  trends: {date: string; fills: number; noShows: number; hours: number}[]
  scoreHistory: {date: string; score: number}[]
}

export interface AgencyRecruiterEntry {
  rank: number
  userId: string
  userName: string
  totalFills: number
  totalPoints: number
  fillRatio: number
  qualityRatio: number
  avgCallToFillMinutes: number
  fillsPerHour: number
}

/**
 * GET /api/metrics/agency/:agencyId/recruiters. A server from S12 on sends the leaderboard's
 * `actorHistory`, `team`, `rows` and `omittedPeople`; an older one sends none of them, and only the
 * legacy fields.
 */
export interface AgencyRecruiterResponse
  extends Partial<Pick<RecruiterLeaderboard, 'actorHistory' | 'team' | 'rows' | 'omittedPeople'>> {
  agencyId: string
  periodStart: string
  periodEnd: string
  /** @deprecated Always 0 from S12 servers: read `team.people`. */
  totalRecruiters: number
  /** @deprecated Always empty from S12 servers: read `rows`. */
  leaderboard: AgencyRecruiterEntry[]
  /** @deprecated Always zeros from S12 servers: read `team`. */
  aggregateMetrics: {
    totalCalls: number
    totalCandidatesReached: number
    totalCandidatesAccepted: number
    totalQualityFills: number
    totalNoShows: number
    avgFillsPerRecruiter: number
    avgPointsPerRecruiter: number
  }
}

export interface AgencyClientEntry {
  companyId: string
  companyName?: string
  orderCount: number
  fillCount: number
  fillSuccessRate: number
  hoursWorked: number
  avgFillTimeMinutes: number
}

export interface AgencyAtRiskClient {
  companyId: string
  companyName?: string
  lastOrderDate: string
  orderDeclinePercent: number
  riskScore: number
}

export interface AgencyClientResponse {
  agencyId: string
  periodStart: string
  periodEnd: string
  totalClients: number
  activeClients: number
  topClients: AgencyClientEntry[]
  atRiskClients: AgencyAtRiskClient[]
}

export interface AgencyTrendsResponse {
  agencyId: string
  periodStart: string
  periodEnd: string
  fillRateTrend: {date: string; value: number}[]
  noShowRateTrend: {date: string; value: number}[]
  scoreTrend: {date: string; value: number}[]
  orderVolumeTrend: {date: string; value: number}[]
  pledgeVolumeTrend: {date: string; value: number}[]
  confirmedVolumeTrend: {date: string; value: number}[]
  hoursTrend: {date: string; value: number}[]
  periodComparison: {
    metric: string
    currentPeriod: number
    previousPeriod: number
    changePercent: number
  }[]
}

export type GroupByPeriod = 'month' | 'quarter' | 'year'

/**
 * Metrics API service for MSP Platform Admin Dashboard
 * Provides access to all metrics and analytics endpoints
 */
class MetricsApi extends BaseApi {
  // =============================================
  // Dashboard Endpoints
  // =============================================

  /**
   * Get comprehensive dashboard summary with all KPIs
   * GET /api/metrics/dashboard/summary
   */
  async getDashboardSummary(startDate: Date, endDate: Date): Promise<DashboardSummaryResponse> {
    try {
      const response = await this.get<DashboardSummaryResponse>('/api/metrics/dashboard/summary', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching dashboard summary:', error)
      throw error
    }
  }

  /**
   * Get earnings summary with period breakdown
   * GET /api/metrics/earnings/summary
   */
  async getEarningsSummary(
    startDate: Date,
    endDate: Date,
    groupBy: GroupByPeriod = 'month',
  ): Promise<EarningsSummaryResponse> {
    try {
      const response = await this.get<EarningsSummaryResponse>('/api/metrics/earnings/summary', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          groupBy,
        },
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching earnings summary:', error)
      throw error
    }
  }

  // =============================================
  // Agency Performance Endpoints
  // =============================================

  /**
   * Get all agencies ranked by composite score
   * GET /api/metrics/agencies/rankings
   */
  async getAgencyRankings(
    startDate: Date,
    endDate: Date,
    limit: number = 20,
  ): Promise<AgencyRankingResponse[]> {
    try {
      const response = await this.get<AgencyRankingResponse[]>('/api/metrics/agencies/rankings', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit,
        },
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agency rankings:', error)
      throw error
    }
  }

  /**
   * Get latest reliability score for an agency
   * GET /api/metrics/agency-score/:agencyId
   */
  async getAgencyScore(agencyId: string): Promise<AgencyReliabilityScore | null> {
    try {
      const response = await this.get<AgencyReliabilityScore | null>(
        `/api/metrics/agency-score/${agencyId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agency score:', error)
      throw error
    }
  }

  /**
   * Get agency score history
   * GET /api/metrics/agency-score/:agencyId/history
   */
  async getAgencyScoreHistory(
    agencyId: string,
    limit: number = 12,
  ): Promise<AgencyReliabilityScore[]> {
    try {
      const response = await this.get<AgencyReliabilityScore[]>(
        `/api/metrics/agency-score/${agencyId}/history`,
        {
          params: {limit},
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agency score history:', error)
      throw error
    }
  }

  /**
   * Get agency leaderboard for a period
   * GET /api/metrics/agency-leaderboard
   */
  async getAgencyLeaderboard(
    startDate: Date,
    endDate: Date,
    limit: number = 10,
  ): Promise<AgencyReliabilityScore[]> {
    try {
      const response = await this.get<AgencyReliabilityScore[]>('/api/metrics/agency-leaderboard', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit,
        },
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agency leaderboard:', error)
      throw error
    }
  }

  // =============================================
  // Recruiter Performance Endpoints
  // =============================================

  /**
   * The platform recruiter leaderboard: everyone who accepted a worker in the period or the prior
   * window, ranked by Total fills (S12). A server from before S12 answers 404.
   * GET /api/metrics/recruiters/leaderboard
   */
  async getRecruiterLeaderboard(
    startDate: Date,
    endDate: Date,
    options: RecruiterLeaderboardOptions = {},
  ): Promise<RecruiterLeaderboard> {
    try {
      const response = await this.get<RecruiterLeaderboard>('/api/metrics/recruiters/leaderboard', {
        params: leaderboardParams(startDate, endDate, options),
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching recruiter leaderboard:', error)
      throw error
    }
  }

  // =============================================
  // Company Retention Endpoints
  // =============================================

  /**
   * Get company retention and health metrics
   * GET /api/metrics/companies/retention
   */
  async getCompanyRetention(startDate: Date, endDate: Date): Promise<CompanyRetentionResponse> {
    try {
      const response = await this.get<CompanyRetentionResponse>(
        '/api/metrics/companies/retention',
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching company retention:', error)
      throw error
    }
  }

  // =============================================
  // Fill Metrics Endpoints
  // =============================================

  /**
   * Get fill ratio for a job order
   * GET /api/metrics/fill-ratio/:jobOrderId
   */
  async getFillRatio(jobOrderId: string, requestedCount: number): Promise<FillRatioResult> {
    try {
      const response = await this.get<FillRatioResult>(`/api/metrics/fill-ratio/${jobOrderId}`, {
        params: {requestedCount},
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching fill ratio:', error)
      throw error
    }
  }

  /**
   * Get fill ratio by agency for a service order
   * GET /api/metrics/fill-ratio-by-agency/:serviceOrderId
   */
  async getFillRatioByAgency(serviceOrderId: string): Promise<FillRatioResult> {
    try {
      const response = await this.get<FillRatioResult>(
        `/api/metrics/fill-ratio-by-agency/${serviceOrderId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching fill ratio by agency:', error)
      throw error
    }
  }

  /**
   * Get quality fill ratio for a job order
   * GET /api/metrics/quality-fill-ratio/:jobOrderId
   */
  async getQualityFillRatio(
    jobOrderId: string,
    requestedCount: number,
  ): Promise<QualityFillRatioResult> {
    try {
      const response = await this.get<QualityFillRatioResult>(
        `/api/metrics/quality-fill-ratio/${jobOrderId}`,
        {
          params: {requestedCount},
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching quality fill ratio:', error)
      throw error
    }
  }

  /**
   * Get quality fill ratio by agency for a period
   * GET /api/metrics/quality-fill-ratio-by-agency/:agencyId
   */
  async getQualityFillRatioByAgency(
    agencyId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<QualityFillRatioResult> {
    try {
      const response = await this.get<QualityFillRatioResult>(
        `/api/metrics/quality-fill-ratio-by-agency/${agencyId}`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching quality fill ratio by agency:', error)
      throw error
    }
  }

  /**
   * Get fill velocity for an agency
   * GET /api/metrics/fill-velocity/:agencyId
   */
  async getFillVelocity(
    agencyId: string,
    startDate: Date,
    endDate: Date,
    benchmark?: number,
  ): Promise<FillVelocityResult> {
    try {
      const response = await this.get<FillVelocityResult>(
        `/api/metrics/fill-velocity/${agencyId}`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(benchmark && {benchmark}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching fill velocity:', error)
      throw error
    }
  }

  // =============================================
  // Attendance & Worker Endpoints
  // =============================================

  /**
   * Check if worker is suspended
   * GET /api/metrics/worker/:workerId/suspended
   */
  async isWorkerSuspended(workerId: string): Promise<WorkerSuspensionStatus> {
    try {
      const response = await this.get<WorkerSuspensionStatus>(
        `/api/metrics/worker/${workerId}/suspended`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error checking worker suspension:', error)
      throw error
    }
  }

  // =============================================
  // Company-Specific Metrics Endpoints
  // =============================================

  /**
   * Check if current user can access company metrics
   * GET /api/metrics/company/:companyId/access
   */
  async checkCompanyMetricsAccess(companyId: string): Promise<CompanyMetricsAccessResponse> {
    try {
      const response = await this.get<CompanyMetricsAccessResponse>(
        `/api/metrics/company/${companyId}/access`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error checking company metrics access:', error)
      throw error
    }
  }

  /**
   * Get company metrics overview (KPI dashboard)
   * GET /api/metrics/company/:companyId/overview
   */
  async getCompanyMetricsOverview(
    companyId: string,
    startDate: Date,
    endDate: Date,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyMetricsOverviewResponse> {
    try {
      const response = await this.get<CompanyMetricsOverviewResponse>(
        `/api/metrics/company/${companyId}/overview`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching company metrics overview:', error)
      throw error
    }
  }

  /**
   * Get company financial metrics
   * GET /api/metrics/company/:companyId/financial
   */
  async getCompanyFinancialMetrics(
    companyId: string,
    startDate: Date,
    endDate: Date,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyFinancialMetricsResponse> {
    try {
      const response = await this.get<CompanyFinancialMetricsResponse>(
        `/api/metrics/company/${companyId}/financial`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching company financial metrics:', error)
      throw error
    }
  }

  /**
   * Get company orders and fill performance metrics
   * GET /api/metrics/company/:companyId/orders
   */
  async getCompanyOrdersMetrics(
    companyId: string,
    startDate: Date,
    endDate: Date,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyOrdersMetricsResponse> {
    try {
      const response = await this.get<CompanyOrdersMetricsResponse>(
        `/api/metrics/company/${companyId}/orders`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching company orders metrics:', error)
      throw error
    }
  }

  /**
   * Get company workforce analytics
   * GET /api/metrics/company/:companyId/workforce
   */
  async getCompanyWorkforceMetrics(
    companyId: string,
    startDate: Date,
    endDate: Date,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyWorkforceMetricsResponse> {
    try {
      const response = await this.get<CompanyWorkforceMetricsResponse>(
        `/api/metrics/company/${companyId}/workforce`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching company workforce metrics:', error)
      throw error
    }
  }

  /**
   * Get company trends and analytics
   * GET /api/metrics/company/:companyId/trends
   */
  async getCompanyTrendsMetrics(
    companyId: string,
    startDate: Date,
    endDate: Date,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyTrendsMetricsResponse> {
    try {
      const response = await this.get<CompanyTrendsMetricsResponse>(
        `/api/metrics/company/${companyId}/trends`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching company trends metrics:', error)
      throw error
    }
  }

  /**
   * Get live attendance for a company site on a specific date.
   * GET /api/metrics/company/:companyId/attendance
   *
   * Returns the full { data, meta } envelope — the caller needs meta.punchSource,
   * meta.punchDataFetchedAt, and meta.totalRosterCount for pagination display.
   */
  async getCompanyAttendance(
    companyId: string,
    siteId: string,
    date: string, // YYYY-MM-DD in site's local timezone
    opts?: {
      departmentId?: string
      page?: number
      limit?: number
      forceRefresh?: boolean
    },
  ): Promise<CompanyAttendanceApiEnvelope> {
    try {
      const response = await this.get<CompanyAttendanceApiEnvelope>(
        `/api/metrics/company/${companyId}/attendance`,
        {
          params: {
            siteId,
            date,
            ...(opts?.departmentId && {departmentId: opts.departmentId}),
            ...(opts?.page !== undefined && {page: opts.page}),
            ...(opts?.limit !== undefined && {limit: opts.limit}),
            ...(opts?.forceRefresh && {forceRefresh: true}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching company attendance:', error)
      throw error
    }
  }

  /**
   * Get all-time paycheck hours for every employee who has worked for this client.
   * GET /api/metrics/company/:companyId/workforce/employee-hours
   * No date range — all-time by design.
   */
  async getCompanyEmployeeHours(
    companyId: string,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyEmployeeHoursResponse> {
    try {
      const response = await this.get<CompanyEmployeeHoursResponse>(
        `/api/metrics/company/${companyId}/workforce/employee-hours`,
        {
          params: {
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching company employee hours:', error)
      throw error
    }
  }

  // =============================================
  // Agency Dashboard Endpoints
  // =============================================

  /**
   * Get agency dashboard summary with all KPIs
   * GET /api/metrics/agency/:agencyId/dashboard/summary
   */
  async getAgencyDashboardSummary(
    agencyId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<AgencyDashboardSummaryResponse> {
    try {
      const response = await this.get<AgencyDashboardSummaryResponse>(
        `/api/metrics/agency/${agencyId}/dashboard/summary`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agency dashboard summary:', error)
      throw error
    }
  }

  /**
   * The agency's recruiter leaderboard: its members today, ranked by Total fills on the orders it can
   * see (S12). The server clamps `limit` to 1–50.
   * GET /api/metrics/agency/:agencyId/recruiters
   */
  async getAgencyRecruiters(
    agencyId: string,
    startDate: Date,
    endDate: Date,
    options: RecruiterLeaderboardOptions = {},
  ): Promise<AgencyRecruiterResponse> {
    try {
      const response = await this.get<AgencyRecruiterResponse>(
        `/api/metrics/agency/${agencyId}/recruiters`,
        {params: leaderboardParams(startDate, endDate, options)},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agency recruiters:', error)
      throw error
    }
  }

  /**
   * Get agency client performance and health
   * GET /api/metrics/agency/:agencyId/clients
   */
  async getAgencyClients(
    agencyId: string,
    startDate: Date,
    endDate: Date,
    limit: number = 10,
  ): Promise<AgencyClientResponse> {
    try {
      const response = await this.get<AgencyClientResponse>(
        `/api/metrics/agency/${agencyId}/clients`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit,
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agency clients:', error)
      throw error
    }
  }

  /**
   * Get agency trends and analytics
   * GET /api/metrics/agency/:agencyId/trends
   */
  async getAgencyTrends(
    agencyId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<AgencyTrendsResponse> {
    try {
      const response = await this.get<AgencyTrendsResponse>(
        `/api/metrics/agency/${agencyId}/trends`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agency trends:', error)
      throw error
    }
  }

  // =============================================
  // Utility Methods
  // =============================================

  /**
   * Get date range for common periods
   */
  static getDateRange(period: 'today' | 'week' | 'month' | 'quarter' | 'year'): DateRange {
    const now = new Date()
    const endDate = new Date(now)
    endDate.setHours(23, 59, 59, 999)

    let startDate: Date

    switch (period) {
      case 'today':
        startDate = new Date(now)
        startDate.setHours(0, 0, 0, 0)
        break
      case 'week':
        startDate = new Date(now)
        startDate.setDate(now.getDate() - 7)
        startDate.setHours(0, 0, 0, 0)
        break
      case 'month':
        startDate = new Date(now)
        startDate.setMonth(now.getMonth() - 1)
        startDate.setHours(0, 0, 0, 0)
        break
      case 'quarter':
        startDate = new Date(now)
        startDate.setMonth(now.getMonth() - 3)
        startDate.setHours(0, 0, 0, 0)
        break
      case 'year':
        startDate = new Date(now)
        startDate.setFullYear(now.getFullYear() - 1)
        startDate.setHours(0, 0, 0, 0)
        break
      default:
        startDate = new Date(now)
        startDate.setMonth(now.getMonth() - 1)
        startDate.setHours(0, 0, 0, 0)
        break
    }

    return {startDate, endDate}
  }

  /**
   * Format a number based on the KPI format type
   */
  static formatKPIValue(value: number, format: DashboardKPI['format']): string {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value)
      case 'percent':
        return `${value.toFixed(1)}%`
      case 'hours':
        return `${value.toLocaleString()} hrs`
      case 'number':
      default:
        return value.toLocaleString()
    }
  }
}

export const metricsApi = new MetricsApi()
